import { IonButton, IonContent, IonPage, ToastOptions, useIonToast } from '@ionic/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import DayPicker from '../components/DayPicker';
import { Header } from '../components/Header';
import { defaultToastOptions, loadingAtom, pointingToEditIdAtom, usersAtom } from '../utils/Global';
import odoo, { standardizedOdooPointing, saveOrCreateWithoutRead, unstandardizedOdooPointing } from '../utils/odoo';

import { IosHeader } from '../components/IosHeader';
import { useEmployees } from '../hooks/useSwr';
import { getMetaData, pointingInitialValue, PointingState } from '../utils/odooUtils';
import './PointingConfirm.scss';
import PointingDetail, { PointingDetailHandle, ViewMode } from '../components/common/PointingDetail';
import { OdooPointing, Pointing, pointingFields, PointingToSaved } from '../types/models';
import { DateTime } from 'luxon';
import { each } from 'lodash/fp';

// TODO : To refactor, small enhancement
export const PointingConfirm: React.FC = () => {
    const [pointingToEditId, setPointingToEditId] = useAtom(pointingToEditIdAtom);
    const { ids: employeeIds } = useEmployees();
    const setLoading = useSetAtom(loadingAtom);

    const [presentToast] = useIonToast();

    const history = useHistory<{ edit?: boolean }>();
    const refPointingDetail = useRef<PointingDetailHandle | null>(null);

    const [selectedDate, setSelectedDate] = useState(DateTime.now().startOf('day'));
    const [isEdited, setIsEdited] = useState(false);
    const [isValid, setIsValid] = useState(false);
    const { mainUser } = useAtomValue(usersAtom);
    const isAdherentOrGeiq = mainUser?.type === 'adherent' || mainUser?.type === 'geiq';

    const [pointing, setPointing] = useState<Pointing | null>(null);

    const onConfirm = async () => {
        if (!refPointingDetail.current) {
            return;
        }

        try {
            setLoading(true);
            const pointing = refPointingDetail.current.getPointing();

            const extraData = await getMetaData();

            await saveOrCreateWithoutRead<PointingToSaved>(
                'hr.madper.app.pointing.time',
                {
                    ...unstandardizedOdooPointing({
                        ...pointing,
                        metadata: { ...pointing.metadata, confirm: { ...pointing.metadata.end, ...extraData } },
                        user_employee_validation_date: DateTime.now(),
                    }),
                    date: pointing.date || pointing.input_time_start?.toISODate() || selectedDate.toISODate(),
                },
                { forwardIp: true, pointingState: 'confirm' }
            );
            setLoading(false);

            setPointingToEditId(null);

            presentToast({
                ...defaultToastOptions,
                message: `Le pointage a bien été ${history.location.state?.edit ? 'modifié' : 'ajouté'}.`,
                duration: 3000,
                color: 'success',
            });
            history.replace('/pointing');
        } catch (e: any) {
            setLoading(false);
            each((errorToast: ToastOptions) => presentToast(errorToast), e.messages);
        }
    };

    useEffect(() => {
        setLoading(true);

        odoo.searchReadOne<OdooPointing>('hr.madper.app.pointing.time', {
            fields: pointingFields,
            domain: pointingToEditId
                ? [['id', '=', pointingToEditId]]
                : [
                      ['employee_id', 'in', employeeIds],
                      ['user_employee_validation_date', '=', false],
                      ['state', '=', PointingState.EndPointing],
                      ['user_adherent_id', '=', false],
                      ['origin_id', '=', false],
                  ],
            sort: 'create_date desc',
        })
            .then((employeePointing) => {
                if (employeePointing) {
                    setPointing(standardizedOdooPointing(employeePointing));
                    setIsEdited(false);
                } else if (isAdherentOrGeiq) {
                    setPointing(pointingInitialValue(selectedDate.toISODate()));
                } else {
                    history.replace('/pointing');
                }
            })
            .catch((e: any) => {
                each((errorToast: ToastOptions) => presentToast(errorToast), e.messages);
            })
            .finally(() => {
                setLoading(false);
            });
        return () => {
            setLoading(false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const title = 'Confirmer pointage';

    return (
        <IonPage id="pointing-confirm-page">
            <IosHeader title={title} />
            <IonContent fullscreen={true}>
                <Header title={title} />
                {pointing && (
                    <>
                        <DayPicker
                            date={selectedDate}
                            onChange={(date) => {
                                setSelectedDate(date);
                            }}
                            employees={employeeIds}
                            disabled={!isAdherentOrGeiq}
                        />
                        <div className="mt-4">
                            <PointingDetail
                                initialPointing={pointing}
                                ref={refPointingDetail}
                                key={pointing.id}
                                onChange={(pointing: Pointing) => {
                                    if (!isEdited) {
                                        setIsEdited(true);
                                    }
                                    setIsValid(
                                        (pointing.mad_id > 0 || (pointing.mad_id === 0 && pointing.other_mad !== '')) &&
                                            (pointing.workplace_id > 0 ||
                                                (pointing.workplace_id === 0 && pointing.other_workplace !== ''))
                                    );
                                }}
                                viewMode={ViewMode.Confirm}
                            />
                        </div>

                        <div className="grid content-center">
                            <div className="ion-text-center madper-button">
                                <IonButton
                                    shape="round"
                                    disabled={!isEdited || !isValid}
                                    onClick={onConfirm}
                                    type="button"
                                    className="btn"
                                    expand="block"
                                >
                                    Valider
                                </IonButton>
                            </div>
                        </div>
                    </>
                )}
            </IonContent>
        </IonPage>
    );
};
